import {noop, range} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import BulkAddRowsToRegistrationTableFlow from 'bulk-import/BulkAddRowsToRegistrationTableFlow';
import DataConnector from 'connect/DataConnector';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import {forwardSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import registerSamples from 'notebook/registration-tables/registerSamples';
import {augmentRegisterOperation} from 'notebook/registration-tables/RegistrationTableColumnDefinitions';
import {
  RegistrationOperation,
  RegistrationOperationType,
  RegistrationTable as RegistrationTableData,
  RegistrationTableBaseProps,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {getSampleDetailsFromRegistrationTable} from 'notebook/registration-tables/RegistrationTableOps';
import RegistrationTableSettingsModal, {
  RegistrationTableSettingsModalValues,
} from 'notebook/registration-tables/RegistrationTableSettingsModal';
import {
  areRequiredSchemasArchived,
  getColumnDefinitionsFromStructuredTableColumns,
  getInsertableColumnInfos,
  getRegistrationTableRows,
  getRowWithDefaults,
  getTableRows,
  isRequiredBatchSchemaDeactivated,
  performColumnOpAndSetLabelingStrategy,
  RegistrationException,
} from 'notebook/registration-tables/RegistrationTableUtil';
import withRegistrationTableUtils, {
  RegistrationTableUtilitiesProps,
} from 'notebook/registration-tables/withRegistrationTableUtils';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import ManageSourcesModal from 'notebook/structured-tables/components/modals/ManageSourcesModal';
import PrintBarcodesButton from 'notebook/structured-tables/components/PrintBarcodesButton';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import TableHeaderPipeDivider from 'notebook/structured-tables/components/TableHeaderPipeDivider';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import prepareImportedRows, {
  ImportedStructuredTableRow,
} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';
import {
  LegacyStructuredTableRow,
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {CollapsedTableReduxActions} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Cells, RowData} from 'notebook/tables/constants/RowConstants';
import {
  Table,
  TableData,
  TableHeaderControlsArgs,
  UPDATE_LOOKUP_FIELD_MESSAGE,
} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import LookupField from 'notebook/tables/excess/functions/lookupField';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {getCombinedErrorString} from 'notebook/tables/excess/Util';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {LookupType} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {handleUpdateLookupFieldValues} from 'notebook/tables/lookup-field/LookupFieldUtil';
import {
  handleRefreshTableData,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {fetchColumnUpdatesForTable} from 'notebook/tables/refresh-table-data/RefreshTableData';
import TableUtil from 'notebook/tables/TableUtil';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import CreateRequestFromSamplesButton from 'requests/creation/CreateRequestFromSamplesButton';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {INPUT_SAMPLES_SOURCE_UUID, StageRun} from 'workflows/WorkflowConstants';

interface ConnectedProps {
  collapsedState: boolean | undefined;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class RegistrationTableConnector extends DataConnector<RegistrationTableProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps: () => ConnectedProps;

  constructor(props: RegistrationTableProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const schemaFields = [
      ...(this.props.tagSchema == null ? [] : this.props.tagSchema.fields),
      ...(this.props.batchSchema == null ? [] : this.props.batchSchema.fields),
    ];
    return {
      collapsedState:
        this.reduxState().ui.collapsedTables[
          createCollapseId(this.props.editorProps.fileId, this.props.tableName)
        ],
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(schemaFields)
      ),
    };
  };
}

interface RegistrationTableProps extends RegistrationTableBaseProps {
  stageRuns: Array<StageRun>;
  registrationRecordListId: string | null;
}

interface State {
  isManageSourcesModalOpen: boolean;
}

/**
 * This is a legacy registration table, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export class RegistrationTable extends React.Component<
  RegistrationTableProps & ConnectedProps & RegistrationTableUtilitiesProps,
  State
> {
  override state: State = {
    isManageSourcesModalOpen: false,
  };

  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  _getRegistrationTableData({
    newColumns,
    newRows,
    labelingStrategy,
  }: {
    newColumns?: Array<StructuredTableColumn>;
    newRows?: Array<LegacyStructuredTableRow>;
    labelingStrategy?: RegistryLabelingStrategy;
  }): RegistrationTableData {
    return {
      ...this.props.utils.getRegistrationTableData({newColumns, newRows, labelingStrategy}),
      registrationRecordListId: this.props.registrationRecordListId,
      type: NoteLinkedObjectType.REGISTRATION_TABLE,
    };
  }

  _mergeRowsAndColumnsIntoTable({
    index,
    newColumns,
    newRows,
    labelingStrategy,
    tableOp,
  }: {
    index: number;
    newColumns: Array<StructuredTableColumn>;
    newRows: Array<LegacyStructuredTableRow>;
    labelingStrategy?: RegistryLabelingStrategy;
    tableOp?: TableOpDiffMap;
  }): void {
    this.props.utils.mergeRowsAndColumnsIntoTable(
      index,
      this._getRegistrationTableData({
        newColumns,
        newRows,
        labelingStrategy,
      }),
      this.props.stageRuns,
      tableOp
    );
  }

  _handleSubmitSettingsModal = (
    registrationTableSettingsModalValues: RegistrationTableSettingsModalValues
  ): void => {
    const {labelingStrategy} = registrationTableSettingsModalValues;
    this.props.editorProps.storeState();
    if (labelingStrategy != null) {
      performColumnOpAndSetLabelingStrategy({
        table: this.props.table,
        performTableOpOnTable: ({op, options}) => {
          this.props.editorProps.performTableOp({tableName: this.props.table.name, op, options});
        },
        labelingStrategy,
      });
    }
  };

  _handleChangeManageSources = (sourceTables: Array<string>, newSources: Array<string>): void => {
    const isWatchingInputTable = sourceTables.concat(newSources).includes(INPUT_SAMPLES_SOURCE_UUID);

    this.props.updateHandler(this.props.index, {isWatchingInputTable}, undefined, {
      [this.props.table.name]: {type: 'eval-table'},
    });

    if (isWatchingInputTable !== this.props.isWatchingInputTable) {
      analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.TOGGLED_WATCHING_ON_REGISTRATION_TABLE, {
        isWatchingInputTable,
      });
    }

    this._handleCloseManageSourcesModal();
  };

  _handleUpdate = (
    index: number,
    {table}: {table: TableData},
    selectors: undefined,
    tableOp?: TableOpDiffMap
  ): void => {
    const newColumns = table.columnConstraints.map((constraint, i) => ({
      ...constraint!.structuredTableColumn!,
      width: table.widths[i],
    }));
    // handle new rows added via pull-down
    const oldRowCells = getTableRows(this.props.rows);
    const newRowCells = table.table.slice();
    const newRowData = table.rowData!.slice();
    for (let i = oldRowCells.length; i < newRowCells.length; i++) {
      newRowCells[i] = getRowWithDefaults(newRowCells[i], this._getBlankRow(i), this._getColumnDefinitions());
      newRowData.push({isFinalized: false});
    }
    this._mergeRowsAndColumnsIntoTable({
      index,
      newColumns,
      newRows: getRegistrationTableRows(newRowCells, newRowData),
      tableOp,
    });
  };

  _getNumRegisteredRows(): number {
    return this.props.rows.filter((row) => row.isFinalized).length;
  }

  _getColumnDefinitions(): Array<ColumnDefinition> {
    return getColumnDefinitionsFromStructuredTableColumns(this.props.structuredTableColumns, {
      tagSchema: this.props.tagSchema,
      batchSchema: this.props.batchSchema,
      stageRuns: this.props.stageRuns,
    });
  }

  _createTable(): Table {
    return {
      ...this.props.table,
      metadata: {labelingStrategy: this.props.labelingStrategy},
      rowLabels: null,
      _rowHeader: null,
    };
  }

  _getEditableRows(): Array<Array<Cell>> {
    return getTableRows(this.props.rows.filter((row) => !row.isFinalized));
  }

  /**
   * May throw RegistrationException if there is a problem to report to the user.
   */
  _getEvaluatedRowsForRegistration(): Array<Array<TypedValue>> {
    const evaluatedRows = [];
    for (const [i, evaluatedRow] of this.props.computedTable.entries()) {
      if (this._isRowFinalized(i)) {
        continue;
      }
      for (const cell of evaluatedRow) {
        if (cell.error) {
          throw new RegistrationException(getCombinedErrorString(cell.error));
        }
      }
      evaluatedRows.push(evaluatedRow.map(({value}) => value));
    }
    return evaluatedRows;
  }

  _getRegisteredRows(): Array<Array<Cell>> {
    return getTableRows(this.props.rows.filter((row) => row.isFinalized));
  }

  /**
   * Return an array of operations to be submitted to the registration API endpoint.
   *
   * May throw RegistrationException if there is a problem to report to the user.
   */
  _getOperations(): Array<RegistrationOperation> {
    assert(
      this.props.tableType === RegistrationTableType.FILE,
      'Only entity registration tables can register'
    );
    const columnDefinitions = this._getColumnDefinitions();
    const operations = [];
    const evaluatedRows = this._getEvaluatedRowsForRegistration();
    for (const evaluatedRow of evaluatedRows) {
      let operation: RegistrationOperation = {
        operationType: RegistrationOperationType.REGISTER_NEW_FILE,
        stageRunId: null,
        tagsDict: {},
      };
      for (const [i, columnDefinition] of columnDefinitions.entries()) {
        operation = augmentRegisterOperation({
          operation,
          typedValue: evaluatedRow[i],
          columnDefinition,
          tagSchema: this.props.tagSchema,
        });
      }
      operations.push(operation);
    }
    return operations;
  }

  _handleRegister = async (): Promise<void> => {
    let operations;
    try {
      operations = this._getOperations();
    } catch (err) {
      if (err instanceof RegistrationException) {
        notifications.error(err.message);
        return;
      } else {
        throw err;
      }
    }

    const originalCollapsedState = this.props.collapsedState;
    CollapsedTableReduxActions.set(this.props.cid, true);

    const tableRowsNumber = this._getNumRegisteredRows() + this._getEditableRows().length;

    try {
      await registerSamples({
        entryId: this.props.editorProps.fileId,
        registrationTableProvenanceUuid: this.props.provenanceUuid,
        operations,
        rowIndicesToRegister: this._getRowIndicesToRegister(tableRowsNumber),
      });
    } catch (err) {
      if (net.isServerRequestError(err)) {
        notifications.stickyNotifyForFriendlyError(err, 'registration-table-fail');
        return;
      }
      throw err;
    } finally {
      assert(originalCollapsedState != null, 'Collapsed state is expected to be set.');
      CollapsedTableReduxActions.set(this.props.cid, originalCollapsedState);
    }
  };

  _getBlankRow(index: number): Array<Cell> {
    const stageRunName = this.props.stageRuns.length === 1 ? this.props.stageRuns[0].name : null;
    return getBlankRow(index, this.props.structuredTableColumns, stageRunName);
  }

  _onImportRows = (importedRows: Array<ImportedStructuredTableRow>): void => {
    const columnDefinitions = this._getColumnDefinitions();
    const newRowCells = prepareImportedRows(
      importedRows,
      columnDefinitions.map((columnDefinition) => ({
        columnDefinition,
        spreadsheetColumnType: getSpreadsheetTypeForColumn(ModelTypeConstants.TAG_SCHEMA, columnDefinition),
      }))
    );
    const oldRows = this.props.rows;
    const startIndex = oldRows.length;
    const endIndex = startIndex + newRowCells.length;
    const newRows = newRowCells.map((cells) => ({cells}));

    const startingRowIndex = oldRows.length;
    const newRowsWithDefaultValues = newRows.map((row, newRowIndex) => {
      const structuredTableRow = this._getBlankRow(startingRowIndex + newRowIndex);
      Object.entries(row.cells).forEach(([colIndex, cell]) => {
        if (!TableUtil.isCellEmpty(cell)) {
          structuredTableRow[Number(colIndex)] = cell;
        }
      });
      return structuredTableRow;
    });

    const rows = [...oldRows, ...newRowsWithDefaultValues.map((cells) => ({cells, isFinalized: false}))];

    this._mergeRowsAndColumnsIntoTable({
      index: this.props.index,
      newColumns: this.props.structuredTableColumns,
      newRows: rows,
      tableOp: {[this.props.table.name]: {type: 'insert-rows', rowIndexes: range(startIndex, endIndex - 1)}},
    });
  };

  _handleImportFromSpreadsheet = (): void => {
    BulkAddRowsToRegistrationTableFlow.open({
      tagSchemaId: this.props.tagSchema.id,
      batchSchemaId:
        this.props.tableType === RegistrationTableType.BATCH ? this.props.tagSchema.batchSchemaId : null,
      columnDefinitions: this._getColumnDefinitions(),
      onImportRows: this._onImportRows,
    });
  };

  _renderAddButton = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed || this._tableIsLocked()) {
      return null;
    }
    return (
      <Dropdown
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FIT_CONTENT}>
            <Option
              icon="upload"
              onSelect={() => {
                this._handleImportFromSpreadsheet();
              }}
            >
              Import from spreadsheet
            </Option>
          </Menu>
        }
      >
        <RichObjectTable.AddButton />
      </Dropdown>
    );
  };

  _handleOpenSettingsModal = async (): Promise<void> => {
    const registrationTableSettingsModalValues = await runModal(
      RegistrationTableSettingsModal,
      {
        labelingStrategy: this.props.labelingStrategy,
        locked: !this._canLabelingStrategyBeChanged(),
        tagSchemaId: this.props.tagSchema.id,
      },
      {title: 'Registration table settings', zIndex: ZINDEX_MODAL}
    );
    if (registrationTableSettingsModalValues != null) {
      this._handleSubmitSettingsModal(registrationTableSettingsModalValues);
    }
  };

  _handleOpenManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: true});
  };

  _handleCloseManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: false});
  };

  /**
   * Only allow changing the labeling strategy if the editor is unlocked and we
   * don't have any registered rows.
   */
  _canLabelingStrategyBeChanged = (): boolean =>
    !(this._tableIsLocked() || this._getRegisteredRows().length > 0);

  _renderSettingsModalButton(): JSX.Element {
    return (
      <Tooltip title="Settings">
        <span>
          <Button
            aria-label="Settings"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            icon="settings"
            disabled={this.props.editor.isProtectedAgainstEdit()}
            onClick={this._handleOpenSettingsModal}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
    );
  }

  _renderManageSourcesModalButton(): JSX.Element | null {
    if (APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)) {
      return null;
    }
    const sources = this.props.isWatchingInputTable ? [INPUT_SAMPLES_SOURCE_UUID] : [];
    return (
      <span>
        <ModalAnchor open={this.state.isManageSourcesModalOpen}>
          {this.state.isManageSourcesModalOpen && (
            <ManageSourcesModal
              initialSourceTableUuids={sources}
              onRequestClose={this._handleCloseManageSourcesModal}
              onRequestSubmit={this._handleChangeManageSources}
            />
          )}
        </ModalAnchor>
        <Tooltip title="Manage sources">
          <span>
            <Button
              aria-label="Manage sources"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className="u-mini-spacing-left"
              disabled={this._tableIsLocked()}
              icon="sources"
              onClick={this._handleOpenManageSourcesModal}
              size={ButtonSize.MINI}
              text={sources.length.toString()}
            />
          </span>
        </Tooltip>
      </span>
    );
  }

  _handleDownloadAsCSV = async (): Promise<void> => {
    const {table: cells, name, columnLabels} = this._createTable();
    const headerRow = columnLabels.map((columnLabel) => columnLabel || 'unknown');
    initiateTextDownload(
      await makeTableCSV({
        table: cells,
        computedTable: this.props.computedTable,
        headerRow,
      }),
      `${name}.csv`
    );
    analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.DOWNLOADED_REGISTRATION_TABLE_AS_CSV);
  };

  _renderExportToCSVButton(): JSX.Element {
    return <SimpleDownloadAsCSVButton onDownloadAsCSV={this._handleDownloadAsCSV} />;
  }

  _renderCreateRequestButton(): JSX.Element {
    const {tagSchema, batchSchema} = this.props;
    const samples = getSampleDetailsFromRegistrationTable(this._getRegistrationTableData({})).map(
      (sample) => ({
        ...sample,
        schema: this.props.tableType === RegistrationTableType.FILE ? tagSchema : batchSchema,
      })
    );

    return (
      <CreateRequestFromSamplesButton
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        samples={samples}
        size={ButtonSize.MINI}
      />
    );
  }

  _renderPrintButton(multiSelectionBox: MultiSelectionBox | null): JSX.Element | null {
    return (
      <>
        <PrintBarcodesButton
          table={this.props.table}
          multiSelectionBox={multiSelectionBox}
          columns={this.props.structuredTableColumns}
          computedTable={this.props.computedTable}
        />
        <TableHeaderPipeDivider />
      </>
    );
  }

  _renderHeaderControls = ({isCollapsed, multiSelectionBox}: TableHeaderControlsArgs): JSX.Element => {
    if (isCollapsed) {
      return this._renderExportToCSVButton();
    }
    const hasEditableRows = this._hasEditableRows();

    let registerTooltipTitle = 'Register';
    if (isRequiredBatchSchemaDeactivated(this.props.tableType, this.props.batchSchema)) {
      registerTooltipTitle = 'Batch schema must be active to register';
    } else if (this._areRequiredSchemasArchived()) {
      registerTooltipTitle = 'Schema must be un-archived to register';
    } else if (!hasEditableRows) {
      registerTooltipTitle = 'Select only unregistered rows to register';
    } else if (this._hasValidationErrorsInRowsToRegister()) {
      registerTooltipTitle = 'Fix validation errors to register';
    }

    const table = this._createTable();

    const doRefresh = async (): Promise<void> => {
      await handleRefreshTableData({
        table,
        computedTable: this.props.computedTable,
        onUpdateTable: ({
          updatedCells,
          columnUpdates,
        }: {
          updatedCells: Cells;
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...table, table: updatedCells, columnConstraints};
          this._handleUpdate(this.props.index, {table: newTable}, undefined, {
            [this.props.table.name]: {type: 'eval-table'},
          });
        },
        fetchColumnUpdates: () =>
          fetchColumnUpdatesForTable({
            entryOrTemplateId: this.props.editorProps.fileId,
            provenanceUuid: this.props.provenanceUuid,
          }),
      });
    };

    return (
      <span className="u-vertical-center-items">
        {this._renderPrintButton(multiSelectionBox)}
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Registration table"
          disabled={this._tableIsLocked()}
        />
        {BenchlingConfig.ENABLE_REQUESTS && this._renderCreateRequestButton()}
        {this._renderExportToCSVButton()}
        {this._renderSettingsModalButton()}
        {this._renderManageSourcesModalButton()}
        <Tooltip title={registerTooltipTitle}>
          <span>
            <Button
              aria-label={registerTooltipTitle}
              buttonStyle={ButtonStyle.SECONDARY}
              className="u-mini-spacing-left"
              disabled={
                this._tableIsLocked() || !hasEditableRows || this._hasValidationErrorsInRowsToRegister()
              }
              icon="register"
              onClick={this._handleRegister}
              size={ButtonSize.MINI}
              text="Register"
            />
          </span>
        </Tooltip>
      </span>
    );
  };

  _isRowFinalized(i: number): boolean {
    return this.props.rows[i] && this.props.rows[i].isFinalized;
  }

  _hasEditableRows(): boolean {
    return Boolean(this._getEditableRows().length);
  }

  _getRowIndicesToRegister(tableRowsNumber: number): Array<number> {
    return _.range(tableRowsNumber).filter((i) => !this._isRowFinalized(i));
  }

  _hasValidationErrorsInRowsToRegister(): boolean {
    const rowsIndicesToRegister = this._getRowIndicesToRegister(this.props.computedTable.length);
    for (const i of rowsIndicesToRegister) {
      const evaluatedRow = this.props.computedTable[i];
      for (const evaluatedCell of evaluatedRow) {
        if (evaluatedCell.error) {
          return true;
        }
      }
    }
    return false;
  }

  _areRequiredSchemasArchived(): boolean {
    return areRequiredSchemasArchived(this.props.tableType, this.props.tagSchema, this.props.batchSchema);
  }

  _getLockedTableWarningType(): LockedTableWarningType {
    if (isRequiredBatchSchemaDeactivated(this.props.tableType, this.props.batchSchema)) {
      return LockedTableWarningType.DEACTIVATED_BATCH_SCHEMA;
    }
    if (this._areRequiredSchemasArchived()) {
      return LockedTableWarningType.ARCHIVED_SCHEMA;
    }
    return LockedTableWarningType.GENERIC;
  }

  _getEditWhileCellLockedWarning = (multiSelectionBox: MultiSelectionBox | null): string => {
    let type;
    if (multiSelectionBox && multiSelectionBox.minI < this._getNumRegisteredRows()) {
      type = LockedCellWarningType.ALREADY_REGISTERED;
    } else {
      type = LockedCellWarningType.GENERIC;
    }
    return getLockedCellWarningMessage(type);
  };

  _tableIsLocked(): boolean {
    return (
      this.props.editorProps.locked ||
      !isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE}) ||
      this.props.tableType === RegistrationTableType.BATCH ||
      this._areRequiredSchemasArchived()
    );
  }

  override render(): JSX.Element {
    const {tagSchema, batchSchema, selectorsById} = this.props;
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );
    Object.assign(allEditorProps, {
      deleteHandler: () => {
        this.props.editor.itemDeleteHandler(this.props.index);
      },
    });
    const table = this._createTable();
    return (
      <TableEditable
        {...allEditorProps}
        locked={this._tableIsLocked()}
        isProtected={this.props.editor.isProtectedAgainstEdit()}
        additionalFunctions={
          APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)
            ? []
            : [
                {
                  name: LookupField.name,
                  asyncOnClick: ({
                    rowData,
                    evaluatedRow,
                    onUpdateSelectedCell,
                  }: {
                    rowData: RowData;
                    evaluatedRow: Array<EvaluatedCell>;
                    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
                  }) =>
                    LookupField.onCreateFormula({
                      entryType: APIUtil.apiIdToType(this.props.editorProps.fileId),
                      stageConfigurationId: null,
                      supportedLookupTypes: [LookupType.STAGE_RUN_INPUT, LookupType.EXPERIMENTAL_CONDITION],
                      rowData,
                      evaluatedRow,
                      columnDefinitions: this._getColumnDefinitions(),
                      onUpdateSelectedCell,
                    }),
                },
              ]
        }
        additionalSettings={
          APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)
            ? []
            : [{name: UPDATE_LOOKUP_FIELD_MESSAGE, applySettingToSelection: handleUpdateLookupFieldValues}]
        }
        canAddRemoveColumns={!this._getNumRegisteredRows()}
        cid={this.props.cid}
        computedTable={this.props.computedTable}
        collapseId={createCollapseId(this.props.editorProps.fileId, table.name)}
        disableFormulas={false}
        disableLegacyHeader={true}
        disableMaximizedTables={allEditorProps.disableMaximizedTables}
        getEditWhileCellLockedWarning={this._getEditWhileCellLockedWarning}
        editWhileTableLockedWarning={getLockedTableWarningMessage(this._getLockedTableWarningType())}
        headerControls={(args) => this._renderHeaderControls(args)}
        leftHeaderControls={(args) => this._renderAddButton(args)}
        icon={this.props.icon}
        indentation={this.props.indentation}
        index={this.props.index}
        insertableColumnInfos={getInsertableColumnInfos(
          tagSchema,
          batchSchema,
          this.props.structuredTableColumns,
          selectorsById
        )}
        isCollapsible={true}
        isLeftAlignColumnHeader={true}
        isMaximized={this.props.isMaximized}
        disableMergingCells={true}
        metadataForRestore={this.props.metadataForRestore}
        mouseDownHandler={this.props.mouseDownHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        multiEditing={this.props.multiEditing}
        openManageSelectorsModal={noop}
        selectorsEnabled={false}
        selectorsLink={RegistrationTable.selectorsLink}
        showFormulaBox={true}
        tableLabel={this.props.tableTypeLabel}
        table={table}
        tableNamespace={this.props.tableNamespace}
        updateHandler={this._handleUpdate}
        {...forwardSearchResultProps(this.props)}
      />
    );
  }
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default RegistrationTableConnector.connect(withRegistrationTableUtils(RegistrationTable));
